import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '@/features/contracts/components/SignaturePad';
import { AddressField } from '@/features/contracts/components/AddressField';
import { RichTextRenderer } from '@/shared/components/RichTextRenderer';
import { ContractTemplate, AddressValue } from '@/shared/types';
import { Check, ChevronDown, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

type FormState = 'loading' | 'not_found' | 'expired' | 'already_signed' | 'form' | 'success';

interface ClientPrefill {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  birth_date?: string | null;
}

interface FormData {
  template: ContractTemplate;
  clientPrefill: ClientPrefill;
  profile: { business_name?: string; practitioner_name?: string } | null;
}

const TOTAL_STEPS = 3;

export default function PublicConsentForm() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<FormState>('loading');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [step, setStep] = useState(0);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState('not_found'); return; }

    fetch(`${FUNCTIONS_URL}/save-public-consent?token=${token}`, {
      headers: {
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'expired') setState('expired');
        else if (data.error === 'already_signed') setState('already_signed');
        else setState('not_found');
        return;
      }
      setFormData(data);
      // Pre-fill from client snapshot — base keys + template alias keys
      if (data.clientPrefill) {
        const pf = data.clientPrefill as ClientPrefill;
        const fullName = [pf.first_name, pf.last_name].filter(Boolean).join(' ');

        // Base snake_case keys (matched by generatePdf globals)
        const initial: Record<string, string> = {};
        if (pf.first_name) initial['first_name'] = pf.first_name;
        if (pf.last_name)  initial['last_name']  = pf.last_name;
        if (pf.email)      initial['email']       = pf.email;
        if (pf.phone)      initial['phone']       = pf.phone;

        // Map onto known template alias keys found in the template fields
        const ALIAS_MAP: Record<string, string> = {
          NOM:             pf.last_name || '',
          PRENOM:          pf.first_name || '',
          NOM_CLIENT:      fullName,
          NOM_COMPLET:     fullName,
          PRENOM_CLIENT:   pf.first_name || '',
          EMAIL:           pf.email || '',
          ADRESSE_EMAIL:   pf.email || '',
          TELEPHONE:       pf.phone || '',
          TEL:             pf.phone || '',
          MOBILE:          pf.phone || '',
          DATE_NAISSANCE:  pf.birth_date || '',
          DATE_DE_NAISSANCE: pf.birth_date || '',
          BIRTH_DATE:      pf.birth_date || '',
        };

        if (data.template?.fields) {
          for (const field of data.template.fields) {
            const upperKey = field.key.toUpperCase();
            if (ALIAS_MAP[upperKey] && !initial[field.key]) {
              initial[field.key] = ALIAS_MAP[upperKey];
            }
          }
        }

        setFieldValues(Object.fromEntries(Object.entries(initial).filter(([, v]) => v)));
      }
      // Initialize default checkboxes
      if (data.template?.checkboxes) {
        const defaults: Record<string, boolean> = {};
        data.template.checkboxes.forEach((cb: any) => {
          if (cb.defaultChecked) defaults[cb.id] = true;
        });
        if (Object.keys(defaults).length) setCheckboxValues(defaults);
      }
      setState('form');
    }).catch(() => setState('not_found'));
  }, [token]);

  const lang = 'fr';
  const template = formData?.template;

  const validate = (): string[] => {
    if (!template) return [];
    const errs: string[] = [];
    if (step === 0) {
      template.fields.forEach(f => {
        if (f.required && f.type !== 'signature' && f.type !== 'photo' && f.type !== 'textarea' && f.type !== 'table' && !fieldValues[f.key]?.trim()) {
          errs.push(f.label[lang]);
        }
        if (f.type === 'email' && fieldValues[f.key]?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValues[f.key].trim())) {
          errs.push(`${f.label[lang]} (format invalide)`);
        }
      });
    }
    if (step === 1) {
      template.checkboxes.forEach(cb => {
        if (cb.required && !checkboxValues[cb.id]) errs.push(cb.label[lang]);
      });
    }
    if (step === 2 && !signatureDataUrl) errs.push('Signature requise');
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }
    if (!token || !template) return;

    const clientName = [fieldValues.first_name, fieldValues.last_name].filter(Boolean).join(' ')
      || fieldValues['NOM_CLIENT'] || '';
    const clientEmail = Object.entries(fieldValues).find(([k, v]) => {
      const f = template.fields.find(f => f.key === k);
      return (f?.type === 'email' || k.toLowerCase().includes('email')) && v?.includes('@');
    })?.[1] || '';

    setSubmitting(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/save-public-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({
          token,
          field_values: fieldValues,
          checkbox_values: checkboxValues,
          signature_data_url: signatureDataUrl,
          client_name: clientName,
          client_email: clientEmail,
          language: lang,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'already_signed') setState('already_signed');
        else setErrors([data.error || 'Erreur lors de la soumission']);
        return;
      }
      setState('success');
    } catch {
      setErrors(['Erreur réseau, veuillez réessayer.']);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (key: string, value: string) => setFieldValues(p => ({ ...p, [key]: value }));

  // ─── Non-form states ──────────────────────────────────────────────────────────

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-3xl bg-primary/15 flex items-center justify-center mx-auto">
            <Check size={36} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-display">Consentement reçu</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Votre formulaire a bien été enregistré. Votre praticien en sera notifié.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">Vous pouvez fermer cette page.</p>
        </div>
      </div>
    );
  }

  if (state === 'already_signed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Check size={28} className="text-primary" />
          </div>
          <h1 className="text-xl font-bold">Déjà signé</h1>
          <p className="text-sm text-muted-foreground">Ce formulaire a déjà été complété. Aucune action requise.</p>
        </div>
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto">
            <AlertCircle size={28} className="text-amber-500" />
          </div>
          <h1 className="text-xl font-bold">Lien expiré</h1>
          <p className="text-sm text-muted-foreground">Ce lien de consentement a expiré. Demandez à votre praticien d'en envoyer un nouveau.</p>
        </div>
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertCircle size={28} className="text-destructive" />
          </div>
          <h1 className="text-xl font-bold">Lien introuvable</h1>
          <p className="text-sm text-muted-foreground">Ce lien de consentement n'existe pas ou a été supprimé.</p>
        </div>
      </div>
    );
  }

  if (!template) return null;

  const businessName = formData?.profile?.business_name || formData?.profile?.practitioner_name || 'Votre praticien';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border/50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{businessName}</p>
            <p className="text-sm font-semibold truncate">{template.name[lang]}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            {[0, 1, 2].map(s => (
              <div key={s} className={`h-1.5 w-6 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-border'}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-24 space-y-5">

        {/* Errors */}
        <AnimatePresence>
          {errors.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex gap-3">
              <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-semibold text-destructive">Champs requis manquants</p>
                <ul className="text-xs text-destructive/80 mt-1 list-disc list-inside">
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

            {/* Step 0: Fields */}
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold">Vos informations</h2>
                {template.fields.map(field => {
                  if (field.type === 'textarea') {
                    const content = field.placeholder?.[lang] || '';
                    if (!content) return null;
                    return (
                      <div key={field.id} className="space-y-1.5">
                        {field.label[lang] && <label className="text-sm font-medium">{field.label[lang]}</label>}
                        <div className="p-3 bg-muted/50 border border-border rounded-xl text-sm leading-relaxed">
                          <RichTextRenderer html={content} />
                        </div>
                      </div>
                    );
                  }

                  if (field.type === 'address') {
                    const addrRaw = fieldValues[field.key];
                    let addrValue: AddressValue;
                    try { addrValue = addrRaw ? JSON.parse(addrRaw) : { country: field.addressConfig?.defaultCountry || 'France', city: '', postalCode: '', street: '', department: '' }; }
                    catch { addrValue = { country: 'France', city: '', postalCode: '', street: '', department: '' }; }
                    return (
                      <AddressField key={field.id} value={addrValue} onChange={val => updateField(field.key, JSON.stringify(val))}
                        language={lang} required={field.required} showDepartment={field.addressConfig?.showDepartment}
                        defaultCountry={field.addressConfig?.defaultCountry} label={field.label[lang]} />
                    );
                  }

                  if (field.type === 'signature') {
                    return (
                      <div key={field.id} className="space-y-1.5">
                        <label className="text-sm font-medium flex items-center gap-1.5">
                          {field.label[lang]}
                          {field.required && <span className="text-destructive text-xs">*</span>}
                        </label>
                        <SignaturePad onSignatureChange={v => updateField(field.key, v || '')} />
                      </div>
                    );
                  }

                  if (field.type === 'table' || field.type === 'photo') return null;

                  return (
                    <div key={field.id} className="space-y-1.5">
                      <label className="text-sm font-medium flex items-center gap-1.5">
                        {field.label[lang]}
                        {field.required && <span className="text-destructive text-xs">*</span>}
                      </label>
                      {field.type === 'select' ? (
                        <div className="relative">
                          <select value={fieldValues[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                            className="w-full p-3.5 bg-card border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none appearance-none pr-10">
                            <option value="">—</option>
                            {(field.options?.[lang] || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                          </select>
                          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                      ) : (
                        <input type={field.type} value={fieldValues[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                          placeholder={field.placeholder?.[lang]}
                          className="w-full p-3.5 bg-card border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Step 1: Sections + checkboxes */}
            {step === 1 && (
              <div className="space-y-5">
                {template.sections.map(section => (
                  <div key={section.id} className="bg-card border border-border rounded-xl p-5 space-y-2">
                    <h3 className="text-base font-bold">{section.title[lang]}</h3>
                    <RichTextRenderer html={section.content[lang]} className="text-sm leading-relaxed text-foreground/90" />
                  </div>
                ))}
                {template.checkboxes.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-bold">Consentements</h2>
                    {template.checkboxes.map(cb => (
                      <label key={cb.id}
                        className={`flex items-start gap-3 p-4 bg-card border-2 rounded-xl cursor-pointer transition-colors ${checkboxValues[cb.id] ? 'border-primary bg-primary/5' : 'border-border'}`}>
                        <input type="checkbox" className="sr-only" checked={!!checkboxValues[cb.id]}
                          onChange={e => setCheckboxValues(p => ({ ...p, [cb.id]: e.target.checked }))} />
                        <div className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center justify-center mt-0.5 transition-colors ${checkboxValues[cb.id] ? 'bg-primary border-primary' : 'border-border'}`}>
                          {checkboxValues[cb.id] && <Check size={14} className="text-primary-foreground" />}
                        </div>
                        <span className="text-sm leading-relaxed flex-1">
                          <RichTextRenderer html={cb.label[lang]} />
                          {cb.required && <span className="text-destructive ml-1">*</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Signature */}
            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-lg font-bold">Votre signature</h2>
                <p className="text-sm text-muted-foreground">
                  En signant, vous confirmez avoir lu et accepté l'ensemble du formulaire de consentement.
                </p>
                <SignaturePad onSignatureChange={v => setSignatureDataUrl(v || '')} />
                {!signatureDataUrl && errors.some(e => e.includes('Signature')) && (
                  <p className="text-sm text-destructive">Veuillez apposer votre signature.</p>
                )}
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border/50 p-4 safe-bottom">
        <div className="max-w-lg mx-auto flex gap-3">
          {step > 0 && (
            <button onClick={() => { setErrors([]); setStep(s => s - 1); window.scrollTo(0, 0); }}
              className="flex items-center gap-1.5 px-4 py-3.5 bg-secondary text-secondary-foreground rounded-xl font-medium">
              <ChevronLeft size={18} />
              Retour
            </button>
          )}
          {step < TOTAL_STEPS - 1 ? (
            <button onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-1.5 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold text-base">
              Suivant <ChevronRight size={18} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold text-base disabled:opacity-50">
              {submitting ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (<><Check size={20} /> Signer et envoyer</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
